<template>
  <div v-if="enabled" class="flex flex-col my-2 justify-center items-center">
    <div :id="widgetId" class="cf-turnstile"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

interface WindowWithTurnstile extends Window {
  turnstile?: {
    render: (element: string | HTMLElement, options: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
    }) => string;
    reset: (widgetId: string) => void;
    remove: (widgetId: string) => void;
  };
}

interface Props {
  sitekey?: string;
}

const props = withDefaults(defineProps<Props>(), {
  sitekey: '',
});

const emit = defineEmits<{
  required: [required: boolean];
  token: [token: string | null];
  error: [errorMessage: string];
}>();

const enabled = ref(false);
const widgetId = ref('');
const currentToken = ref<string | null>(null);

function getSitekey(): string {
  // Check prop first - ensure it's a string
  if (props.sitekey && typeof props.sitekey === 'string' && props.sitekey.trim()) {
    return props.sitekey.trim();
  }

  // Check for global variable
  const windowWithTurnstile = window as unknown as WindowWithTurnstile & {
    TURNSTILE_SITEKEY?: string | unknown;
  };
  if (windowWithTurnstile.TURNSTILE_SITEKEY && typeof windowWithTurnstile.TURNSTILE_SITEKEY === 'string') {
    return windowWithTurnstile.TURNSTILE_SITEKEY.trim();
  }

  // Check for meta tag
  const metaTag = document.querySelector('meta[name="turnstile-sitekey"]');
  if (metaTag) {
    const content = metaTag.getAttribute('content');
    if (content && typeof content === 'string') {
      return content.trim();
    }
  }

  return '';
}

function init() {
  const windowWithTurnstile = window as unknown as WindowWithTurnstile;
  
  // Check if Turnstile is available
  if (!windowWithTurnstile.turnstile) {
    emit('required', false);
    return;
  }

  const sitekey = getSitekey();
  if (!sitekey || typeof sitekey !== 'string') {
    emit('required', false);
    return;
  }

  // Turnstile is available and sitekey is found
  enabled.value = true;
  emit('required', true);

  // Generate a unique widget ID
  const tempWidgetId = `turnstile-widget-${Date.now()}`;
  widgetId.value = tempWidgetId;

  // Wait for next tick to ensure the DOM element exists
  nextTick(() => {
    const element = document.getElementById(tempWidgetId);
    if (!element) {
      return;
    }

    try {
      // Ensure sitekey is a string before passing to Turnstile
      const sitekeyString = String(sitekey).trim();
      if (!sitekeyString) {
        throw new Error('Invalid sitekey: must be a non-empty string');
      }

      const renderedWidgetId = windowWithTurnstile.turnstile!.render(element, {
        sitekey: sitekeyString,
        callback: (token: string) => {
          currentToken.value = token;
          emit('token', token);
        },
        'error-callback': () => {
          currentToken.value = null;
          emit('token', null);
          emit('error', 'Verification failed. Please try again.');
        },
        'expired-callback': () => {
          currentToken.value = null;
          emit('token', null);
        },
      });
      widgetId.value = renderedWidgetId;
    } catch (e) {
      console.error('Failed to initialize Turnstile:', e);
      enabled.value = false;
      emit('required', false);
    }
  });
}

function reset() {
  if (enabled.value && widgetId.value) {
    const windowWithTurnstile = window as unknown as WindowWithTurnstile;
    if (windowWithTurnstile.turnstile) {
      try {
        windowWithTurnstile.turnstile.reset(widgetId.value);
        currentToken.value = null;
        emit('token', null);
      } catch (e) {
        // Ignore errors
      }
    }
  }
}

function cleanup() {
  if (enabled.value && widgetId.value) {
    const windowWithTurnstile = window as unknown as WindowWithTurnstile;
    if (windowWithTurnstile.turnstile) {
      try {
        windowWithTurnstile.turnstile.remove(widgetId.value);
      } catch (e) {
        // Ignore errors during cleanup
      }
    }
  }
}

onMounted(() => {
  init();
});

onBeforeUnmount(() => {
  cleanup();
});

defineExpose({
  reset,
});
</script>

<style scoped>
</style>

